import { c as _c } from "react/compiler-runtime";
import { buildConfluentAllStarCaseName } from "@/configuration/groups/confluent-all-star/case-name";
import { CONFLUENT_MISSING_PHONE_NOTE_ID } from "@/configuration/groups/confluent-all-star/chart-note-ids";
import { DecisionMetadata, outputId, recordOrigin, ReferralWorkflowTimeline, stepStatus, type ReferralWorkflowStep } from "@/configuration/groups/confluent-all-star/components/referral-timeline";
import { CONFLUENT_ALL_STAR_TIMEZONE } from "@/configuration/groups/confluent-all-star/configuration";
import { getConfluentReferralStepKeys, type ConfluentReferralStepKey } from "@/configuration/groups/confluent-all-star/workflows/step-order";
import { FieldLayout, type Field } from "@/src/components/cards/common";
import { DiagnosisListTable } from "@/src/components/cards/diagnosis-list-table";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { ReferralExportResult } from "@/src/components/tasks/referral-export-result";
import { WorkflowStepInformationNeeded } from "@/src/components/tasks/workflow-step-information-needed";
import { WorkflowStepResumeButton } from "@/src/components/tasks/workflow-step-resume-button";
import { formatDateMmDdYyyy, getCurrentDateTimeInTimezone } from "@/src/lib/utils/date-and-phone-utils";
import { REFERRAL_INELIGIBLE_NOTE_ID, REFERRAL_UPLOAD_NOTE_ID } from "@/src/lib/workflows/steps/ehr/clinicient-chart-note-ids";
import { StepKey } from "@/src/lib/workflows/steps/types";
import type { Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { TaskStatus, type Task } from "@/src/models/task";
import type { TaskApproval } from "@/src/models/task-approval";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
type WrittenChartNote = {
  id: string;
  noteEhrId: string;
  note: string;
};
function chartNoteById(step: TaskStep | undefined, id: string): WrittenChartNote | null {
  const notes = step?.output?.notes;
  if (!Array.isArray(notes)) return null;
  return notes.find((note): note is WrittenChartNote => typeof note?.id === "string" && note.id === id && typeof note?.noteEhrId === "string" && typeof note?.note === "string") ?? null;
}
function formatReferringProvider(provider: Referral["referringProvider"]): string | null {
  if (!provider) return null;
  const name = [provider.givenName, provider.familyName].filter(Boolean).join(" ").trim();
  const npi = provider.npiNumber ? `NPI ${provider.npiNumber}` : null;
  return [name || null, npi].filter(Boolean).join(" · ") || null;
}
export function ConfluentReferralSteps(t0) {
  const $ = _c(119);
  const {
    task,
    taskSteps,
    referral,
    pendingApproval
  } = t0;
  let authorizationUploadNeedsReport;
  let hasInformationNeededStep;
  let isCompleted;
  let needsAttention;
  let note;
  let steps;
  let uploadNeedsReport;
  let workflowContinuing;
  if ($[0] !== pendingApproval || $[1] !== referral || $[2] !== task || $[3] !== taskSteps) {
    const orderedTaskSteps = taskSteps.toSorted(_temp);
    const findStep = key => orderedTaskSteps.find(step => step.stepKey === key);
    const patientStep = findStep(StepKey.ENSURE_PATIENT);
    const inheritanceStep = findStep(StepKey.CLINICIENT_SAME_DAY_CASE_INHERITANCE);
    const inheritedCaseId = outputId(inheritanceStep, "inheritedCaseEhrId");
    const inherited = inheritanceStep?.output?.inherited === true;
    const phoneInheritanceStep = findStep(StepKey.CLINICIENT_SAME_DAY_PHONE_INHERITANCE);
    const faxStep = findStep(StepKey.REQUIRE_PATIENT_PHONE);
    const faxSkipped = faxStep?.output?.outcome === "skipped";
    const faxId = outputId(faxStep, "faxId");
    const caseStep = findStep(StepKey.CLINICIENT_ENSURE_CASE);
    const configureStep = findStep(StepKey.CLINICIENT_CONFIGURE_CASE);
    const uploadStep = findStep(StepKey.CLINICIENT_CASE_DOCUMENT_UPLOAD);
    const noteStep = findStep(StepKey.CLINICIENT_DOCUMENT_NOTE);
    const insuranceStep = findStep(StepKey.CLINICIENT_SYNC_INSURANCE);
    const authorizationStep = findStep(StepKey.CLINICIENT_ENSURE_AUTHORIZATION);
    const authorizationUploadStep = findStep(StepKey.CLINICIENT_AUTHORIZATION_DOCUMENT_UPLOAD);
    const authorizationTaskStep = findStep(StepKey.CLINICIENT_AUTHORIZATION_TASK);
    const authorizationId = outputId(authorizationStep, "authorizationEhrId");
    const authorizationDocumentId = outputId(authorizationUploadStep, "documentEhrId");
    const authorizationTaskId = outputId(authorizationTaskStep, "taskEhrId");
    const authorizationSkipped = authorizationStep?.output?.outcome === "skipped";
    const authorizationUploadSkipped = authorizationUploadStep?.output?.outcome === "skipped";
    const authorizationUploadRejected = authorizationUploadStep?.output?.outcome === "request_rejected";
    const authorizationTaskSkipped = authorizationTaskStep?.output?.outcome === "skipped";
    const exportStep = findStep(StepKey.REFERRAL_SHEET_EXPORT);
    const exportRange = outputId(exportStep, "updatedRange");
    const exportSkippedForExistingAppointment = exportRange === "skipped:existing-appointment";
    const patientId = outputId(patientStep, "patientEhrId");
    const caseId = outputId(caseStep, "patientCaseEhrId");
    const documentId = outputId(uploadStep, "documentEhrId");
    const uploadRejected = uploadStep?.output?.outcome === "request_rejected";
    uploadNeedsReport = uploadRejected || uploadStep?.status === TaskStepStatus.FAILED;
    authorizationUploadNeedsReport = authorizationUploadRejected || authorizationUploadStep?.status === TaskStepStatus.FAILED;
    const noteSkipped = noteStep?.output?.outcome === "request_rejected";
    const uploadNote = chartNoteById(noteStep, REFERRAL_UPLOAD_NOTE_ID);
    const noteId = uploadNote?.noteEhrId ?? null;
    const ineligibleNote = chartNoteById(noteStep, REFERRAL_INELIGIBLE_NOTE_ID);
    const missingPhoneNote = chartNoteById(noteStep, CONFLUENT_MISSING_PHONE_NOTE_ID);
    const insurancePlanId = outputId(insuranceStep, "insurancePlanId");
    const existingCase = caseStep?.output?.created === false;
    const insuranceSkipped = insuranceStep?.output?.outcome === "skipped";
    const lastStep = orderedTaskSteps.at(-1);
    const onHold = task.status === TaskStatus.AWAITING_INFORMATION || task.status === TaskStatus.REVIEW_NEEDED;
    const configureHeld = onHold && configureStep !== undefined && configureStep.id === lastStep?.id;
    let t1;
    if ($[12] !== referral.section?.reviewedContent) {
      t1 = referral.section?.reviewedContent ?? {};
      $[12] = referral.section?.reviewedContent;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    const reviewedContent = t1 as Record<string, unknown>;
    let t2;
    if ($[14] !== reviewedContent) {
      t2 = key_0 => {
        const value = reviewedContent[key_0];
        return typeof value === "string" && value.trim() ? value : undefined;
      };
      $[14] = reviewedContent;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    const reviewedString = t2;
    let t3;
    if ($[16] !== reviewedContent) {
      t3 = key_1 => typeof reviewedContent[key_1] === "boolean" ? reviewedContent[key_1] as boolean : undefined;
      $[16] = reviewedContent;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    const reviewedBoolean = t3;
    const priorAuthorization = task.priorAuthorization;
    const bodyPart = reviewedString("bodyPart") ?? null;
    const serviceLine = referral.serviceLine;
    const dateOfOnset = reviewedString("dateOfOnset") ?? null;
    const reasonForReferral = referral.reasonForReferral;
    const primaryEhrPayerName = reviewedString("primaryEhrPayerName") ?? reviewedString("ehrPayerName") ?? null;
    let t4;
    if ($[18] !== reviewedString) {
      t4 = reviewedString("secondaryEhrPayerName") ?? null;
      $[18] = reviewedString;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    const secondaryEhrPayerName = t4;
    let t5;
    if ($[20] !== reviewedBoolean) {
      t5 = reviewedBoolean("isWorkersComp") ?? false;
      $[20] = reviewedBoolean;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    const isWorkersComp = t5;
    const isPostOpReferral = reviewedBoolean("isPostOpReferral") ?? null;
    const isPostOpPriorAuth = reviewedBoolean("isPostOpPriorAuth") ?? priorAuthorization?.isPostOpPriorAuth ?? null;
    const isContinuationOfCare = reviewedBoolean("isContinuationOfCare") === true;
    const numberOfVisits = referral.numberOfVisits;
    const authorizationNumber = referral.authorizationNumber ?? priorAuthorization?.authorizationNumber ?? null;
    let t6;
    if ($[22] !== priorAuthorization?.startDate || $[23] !== reviewedString) {
      t6 = reviewedString("startDate") ?? priorAuthorization?.startDate ?? null;
      $[22] = priorAuthorization?.startDate;
      $[23] = reviewedString;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    const authorizationStart = t6;
    let t7;
    if ($[25] !== priorAuthorization?.endDate || $[26] !== reviewedString) {
      t7 = reviewedString("endDate") ?? priorAuthorization?.endDate ?? null;
      $[25] = priorAuthorization?.endDate;
      $[26] = reviewedString;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    const authorizationEnd = t7;
    const diagnoses = referral.diagnosisList ?? (referral.diagnosisCode || referral.diagnosisText ? [{
      diagnosisCode: referral.diagnosisCode,
      diagnosisCodeType: referral.diagnosisCodeType ?? null,
      diagnosisText: referral.diagnosisText
    }] : []);
    const patient = referral.patient;
    const patientHasPhone = [patient.mobilePhoneNumber, patient.homePhoneNumber, patient.workPhoneNumber].some(_temp2);
    const generatedCaseName = buildConfluentAllStarCaseName({
      diagnosisList: diagnoses.length ? diagnoses : null,
      reasonForReferral,
      bodyPart,
      primaryEhrPayerName,
      serviceLine,
      dateOfBirth: patient.dateOfBirth,
      isPostOpReferral,
      isPostOpPriorAuth
    });
    const syncedCaseName = outputId(caseStep, "caseName") ?? generatedCaseName;
    let t8;
    if ($[28] !== referral.dateOfReferral) {
      t8 = formatDateMmDdYyyy(referral.dateOfReferral);
      $[28] = referral.dateOfReferral;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    const referralDate = t8;
    const documentDescription = referralDate ? `${referralDate} Referral` : "Referral";
    const authorizationTaskAction = isContinuationOfCare ? "Re-Authorization" : "Authorization";
    const authorizationTaskDueDate = getCurrentDateTimeInTimezone(CONFLUENT_ALL_STAR_TIMEZONE).plus({
      days: 1
    }).toFormat("MM/dd/yyyy");
    let t9;
    if ($[30] !== reasonForReferral) {
      t9 = () => reasonForReferral;
      $[30] = reasonForReferral;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    let t10;
    if ($[32] !== referral.referringProvider) {
      t10 = () => formatReferringProvider(referral.referringProvider);
      $[32] = referral.referringProvider;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    const caseIntakeFields = [{
      key: "caseName",
      label: "Case Name",
      getValue: () => syncedCaseName
    }, {
      key: "bodyPart",
      label: "Body Part",
      getValue: () => bodyPart
    }, {
      key: "dateOfOnset",
      label: "Date of Onset",
      type: "date",
      getValue: () => dateOfOnset
    }, {
      key: "reasonForReferral",
      label: "Reason for Referral",
      colSpan: 2,
      getValue: t9
    }, {
      key: "referringProvider",
      label: "Referring Provider",
      getValue: t10
    }];
    const isPostOp = isPostOpReferral === true || isPostOpPriorAuth === true;
    let t11;
    if ($[34] !== serviceLine) {
      t11 = () => serviceLine;
      $[34] = serviceLine;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    let t12;
    if ($[36] !== referral.receivingLocation?.name) {
      t12 = () => referral.receivingLocation?.name ?? null;
      $[36] = referral.receivingLocation?.name;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    let t13;
    if ($[38] !== isWorkersComp) {
      t13 = () => isWorkersComp ? "Yes" : "No";
      $[38] = isWorkersComp;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    let t14;
    if ($[40] !== referral.referringProvider) {
      t14 = () => formatReferringProvider(referral.referringProvider);
      $[40] = referral.referringProvider;
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    const caseDetailFields = [{
      key: "dateOfOnset",
      label: "Date of Onset",
      type: "date",
      getValue: () => dateOfOnset
    }, {
      key: "surgicalDate",
      label: "Surgical Date",
      type: "date",
      hideIfEmpty: true,
      getValue: () => isPostOp ? dateOfOnset : null
    }, {
      key: "serviceLine",
      label: "Service Line",
      getValue: t11
    }, {
      key: "facility",
      label: "Facility",
      getValue: t12
    }, {
      key: "isWorkersComp",
      label: "Workers' Comp",
      getValue: t13
    }, {
      key: "referringProvider",
      label: "Referring Provider",
      getValue: t14
    }];
    let t15;
    if ($[42] !== referral.referringLocation?.name) {
      t15 = () => referral.referringLocation?.name ?? null;
      $[42] = referral.referringLocation?.name;
      $[43] = t15;
    } else {
      t15 = $[43];
    }
    let t16;
    if ($[44] !== referral.primaryCoverage?.subscriberId) {
      t16 = () => referral.primaryCoverage?.subscriberId ?? null;
      $[44] = referral.primaryCoverage?.subscriberId;
      $[45] = t16;
    } else {
      t16 = $[45];
    }
    let t17;
    if ($[46] !== referral.primaryCoverage?.groupNumber) {
      t17 = () => referral.primaryCoverage?.groupNumber ?? null;
      $[46] = referral.primaryCoverage?.groupNumber;
      $[47] = t17;
    } else {
      t17 = $[47];
    }
    const primaryInsuranceFields = [{
      key: "referringLocation",
      label: "Referring Provider Location",
      colSpan: 3,
      getValue: t15
    }, {
      key: "primaryEhrPayerName",
      label: "Clinicient Insurance Name",
      getValue: () => primaryEhrPayerName
    }, {
      key: "primarySubscriber",
      label: "Subscriber Number",
      getValue: t16
    }, {
      key: "primaryGroup",
      label: "Group Number",
      getValue: t17
    }];
    let t18;
    if ($[48] !== secondaryEhrPayerName) {
      t18 = {
        key: "secondaryEhrPayerName",
        label: "Clinicient Insurance Name",
        getValue: () => secondaryEhrPayerName
      };
      $[48] = secondaryEhrPayerName;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    let t19;
    if ($[50] !== referral.secondaryCoverage?.subscriberId) {
      t19 = {
        key: "secondarySubscriber",
        label: "Subscriber Number",
        getValue: () => referral.secondaryCoverage?.subscriberId ?? null
      };
      $[50] = referral.secondaryCoverage?.subscriberId;
      $[51] = t19;
    } else {
      t19 = $[51];
    }
    let t20;
    if ($[52] !== referral.secondaryCoverage?.groupNumber) {
      t20 = {
        key: "secondaryGroup",
        label: "Group Number",
        getValue: () => referral.secondaryCoverage?.groupNumber ?? null
      };
      $[52] = referral.secondaryCoverage?.groupNumber;
      $[53] = t20;
    } else {
      t20 = $[53];
    }
    let t21;
    if ($[54] !== t18 || $[55] !== t19 || $[56] !== t20) {
      t21 = [t18, t19, t20];
      $[54] = t18;
      $[55] = t19;
      $[56] = t20;
      $[57] = t21;
    } else {
      t21 = $[57];
    }
    const secondaryInsuranceFields = t21;
    let t22;
    if ($[58] !== authorizationNumber) {
      t22 = {
        key: "authorizationNumber",
        label: "Authorization Number",
        getValue: () => authorizationNumber
      };
      $[58] = authorizationNumber;
      $[59] = t22;
    } else {
      t22 = $[59];
    }
    let t23;
    if ($[60] !== authorizationStart) {
      t23 = {
        key: "authorizationStart",
        label: "Start Date",
        type: "date",
        getValue: () => authorizationStart
      };
      $[60] = authorizationStart;
      $[61] = t23;
    } else {
      t23 = $[61];
    }
    let t24;
    if ($[62] !== authorizationEnd) {
      t24 = {
        key: "authorizationEnd",
        label: "End Date",
        type: "date",
        getValue: () => authorizationEnd
      };
      $[62] = authorizationEnd;
      $[63] = t24;
    } else {
      t24 = $[63];
    }
    let t25;
    if ($[64] !== numberOfVisits) {
      t25 = {
        key: "numberOfVisits",
        label: "Number of Visits",
        getValue: () => numberOfVisits != null ? String(numberOfVisits) : null
      };
      $[64] = numberOfVisits;
      $[65] = t25;
    } else {
      t25 = $[65];
    }
    let t26;
    if ($[66] !== t22 || $[67] !== t23 || $[68] !== t24 || $[69] !== t25) {
      t26 = [t22, t23, t24, t25];
      $[66] = t22;
      $[67] = t23;
      $[68] = t24;
      $[69] = t25;
      $[70] = t26;
    } else {
      t26 = $[70];
    }
    const authorizationFields = t26;
    let t27;
    if ($[71] !== authorizationTaskAction) {
      t27 = () => authorizationTaskAction;
      $[71] = authorizationTaskAction;
      $[72] = t27;
    } else {
      t27 = $[72];
    }
    const authorizationTaskFields = [{
      key: "action",
      label: "Action",
      getValue: t27
    }, {
      key: "dueDate",
      label: "Due Date",
      getValue: () => authorizationTaskDueDate
    }, {
      key: "priority",
      label: "Priority",
      getValue: _temp3
    }, {
      key: "role",
      label: "Assign to Role",
      getValue: _temp4
    }, {
      key: "case",
      label: "Case",
      getValue: () => syncedCaseName
    }];
    let t28;
    if ($[73] !== referral.diagnosisCode || $[74] !== referral.diagnosisText) {
      t28 = referral.diagnosisCode ? {
        diagnosisCode: referral.diagnosisCode,
        diagnosisText: referral.diagnosisText
      } : null;
      $[73] = referral.diagnosisCode;
      $[74] = referral.diagnosisText;
      $[75] = t28;
    } else {
      t28 = $[75];
    }
    const diagnosisFallback = t28;
    const t29 = phoneInheritanceStep?.status === TaskStepStatus.COMPLETED ? patientHasPhone ? "Phone number on file" : "Wait ended without a phone number; the next step requests one by fax" : phoneInheritanceStep?.status === TaskStepStatus.PAUSED ? "Waiting for a same-day referral to supply a phone number" : "Not run yet";
    let t30;
    if ($[76] !== t29) {
      t30 = <DecisionMetadata items={[{
        label: "Outcome",
        value: t29
      }]} />;
      $[76] = t29;
      $[77] = t30;
    } else {
      t30 = $[77];
    }
    let t31;
    if ($[78] !== patient) {
      t31 = <PatientCard patient={patient} />;
      $[78] = patient;
      $[79] = t31;
    } else {
      t31 = $[79];
    }
    let t32;
    if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <p className="font-medium">All of the following must be true for a case to match the incoming referral:</p>;
      $[80] = t32;
    } else {
      t32 = $[80];
    }
    let t33;
    if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <div>{t32}<ol className="mt-1 list-decimal space-y-1 pl-5 text-muted-foreground"><li>The case is Active.</li><li>The patient has had an appointment for the case within the past 90 days or has one upcoming in the next 90 days, or the case was created within the past 30 days.</li><li><code>serviceLine</code> from intake matches the Discipline in Clinicient (i.e. both Physical Therapy).</li><li>Body part, diagnosis, and reason for referral are similar to the body part in the existing case name and the ICD-10 codes listed on the case.</li></ol><p className="mt-1 text-muted-foreground">If multiple cases match, choose the one with the more recent associated appointment.</p></div>;
      $[81] = t33;
    } else {
      t33 = $[81];
    }
    let t34;
    if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <p className="font-medium">ICD-10 codes</p>;
      $[82] = t34;
    } else {
      t34 = $[82];
    }
    let t35;
    if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = <p>In the Attachments section of the patient chart, upload the document with:</p>;
      $[83] = t35;
    } else {
      t35 = $[83];
    }
    let t36;
    let t37;
    if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <div><dt className="inline font-medium">Attachment Type: </dt><dd className="inline">Case Record</dd></div>;
      t37 = <dt className="inline font-medium">Case: </dt>;
      $[84] = t36;
      $[85] = t37;
    } else {
      t36 = $[84];
      t37 = $[85];
    }
    const t38 = <div>{t37}<dd className="inline">{syncedCaseName}</dd></div>;
    let t39;
    if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
      t39 = <dt className="inline font-medium">Description: </dt>;
      $[86] = t39;
    } else {
      t39 = $[86];
    }
    let t40;
    if ($[87] !== documentDescription) {
      t40 = <div>{t39}<dd className="inline">{documentDescription}</dd></div>;
      $[87] = documentDescription;
      $[88] = t40;
    } else {
      t40 = $[88];
    }
    let t41;
    if ($[89] !== t38 || $[90] !== t40) {
      t41 = <dl className="space-y-1 [overflow-wrap:anywhere]">{t36}{t38}{t40}</dl>;
      $[89] = t38;
      $[90] = t40;
      $[91] = t41;
    } else {
      t41 = $[91];
    }
    const definitions = {
      [StepKey.CLINICIENT_SAME_DAY_PHONE_INHERITANCE]: {
        title: "Hold for a same-day phone number",
        status: stepStatus(phoneInheritanceStep, phoneInheritanceStep?.status === TaskStepStatus.COMPLETED),
        summary: "When a referral has no phone number (on file or in Clinicient), it briefly holds so another document received the same day for this patient can supply one; otherwise it proceeds normally.",
        content: t30
      },
      [StepKey.REQUIRE_PATIENT_PHONE]: {
        title: "Check required patient information",
        status: stepStatus(faxStep, faxId !== null || faxSkipped, onHold && faxStep?.id === lastStep?.id, faxSkipped),
        summary: faxId ? "The patient is missing a phone number. A fax has been sent to the sender requesting it. No action is needed here; the referral continues to be processed." : faxSkipped ? "The patient has all required information." : "The patient information check has not completed yet.",
        content: <DecisionMetadata items={[{
          label: "Fax recipient",
          value: outputId(faxStep, "targetFaxNumber") ?? "Not recorded"
        }, {
          label: "Fax record",
          value: faxId ?? "Not recorded"
        }]} />
      },
      [StepKey.ENSURE_PATIENT]: {
        title: "Confirm patient chart exists in Clinicient with the following demographic information",
        status: stepStatus(patientStep, patientId !== null),
        summary: "For existing patients, only mobile phone number must be updated to reflect the information shown here. For new patients, ensure all information below is included in the chart.",
        content: <div className="space-y-3">{t31}<DecisionMetadata items={[{
            label: "Clinicient patient ID",
            value: patientId ?? "Not recorded"
          }, {
            label: "Patient",
            value: recordOrigin(patientStep?.output?.created)
          }]} /></div>
      },
      [StepKey.CLINICIENT_SAME_DAY_CASE_INHERITANCE]: {
        title: "Inherit case from a same-day referral",
        status: stepStatus(inheritanceStep, typeof inheritanceStep?.output?.inherited === "boolean"),
        summary: inherited ? "This referral had no diagnosis details, so it adopted the Clinicient case and diagnosis of another referral received the same day for this patient." : "This step only applies when a referral has no diagnosis details and another referral received the same day already has a case. Otherwise the referral is matched or created normally in the next step.",
        content: <DecisionMetadata items={[{
          label: "Outcome",
          value: inherited ? "Inherited a same-day referral's case" : inheritanceStep?.status === TaskStepStatus.PAUSED ? "Waiting for a same-day referral to finish setting up its case" : "Not applicable"
        }, {
          label: "Inherited case",
          value: inheritedCaseId ?? "Not recorded"
        }]} />
      },
      [StepKey.CLINICIENT_ENSURE_CASE]: {
        title: "Find or create the referral case",
        status: stepStatus(caseStep, caseId !== null),
        summary: "Match the referral to an existing Clinicient case or create a new case when appropriate. Ambiguous matches require review.",
        content: <div className="space-y-4 text-sm">{t33}<FieldLayout hideEmptyFields={false} fields={caseIntakeFields} patient={referral} /><div className="space-y-1">{t34}<DiagnosisListTable diagnoses={diagnoses} fallbackDiagnosis={diagnosisFallback} /></div><DecisionMetadata items={[{
            label: "Clinicient case ID",
            value: caseId ?? "Not recorded"
          }, {
            label: "Case",
            value: recordOrigin(caseStep?.output?.created)
          }]} /></div>
      },
      [StepKey.CLINICIENT_CONFIGURE_CASE]: {
        title: "Add case details",
        status: stepStatus(configureStep, caseId !== null, configureHeld, existingCase),
        summary: existingCase ? "Preserve the matched case\u2019s existing configuration." : "Ensure that all details below are added in the Case Details, Referral, and Diagnosis Codes sections of the case. Also ensure that \"Authorized to Received Payment\" is selected and that the \"Work Related\" checkbox is selected if the referral is from worker's comp. Add company if needed. Add the Date of Onset if listed, and if the condition is Post-Op, also add the Date of Onset to the Surgical Date field.",
        content: existingCase ? <p className="text-sm">Verify the case details in Clinicient before completing the task.</p> : <div className="space-y-3"><FieldLayout hideEmptyFields={false} fields={caseDetailFields} patient={referral} /><div className="space-y-1"><p className="text-sm font-medium">Diagnosis codes</p><DiagnosisListTable diagnoses={diagnoses} fallbackDiagnosis={diagnosisFallback} /></div></div>
      },
      [StepKey.CLINICIENT_CASE_DOCUMENT_UPLOAD]: {
        title: "Upload the referral document as a case record",
        status: stepStatus(uploadStep, documentId !== null),
        summary: uploadRejected ? "The document upload was rejected. The workflow continued." : "Upload the referral document to the case as a Case Record attachment.",
        content: <div className="space-y-3 text-sm">{t35}{t41}<DecisionMetadata items={[{
            label: "Clinicient document ID",
            value: documentId ?? "Not recorded"
          }]} /></div>
      },
      [StepKey.CLINICIENT_DOCUMENT_NOTE]: {
        title: "Add note(s) to patient chart",
        status: stepStatus(noteStep, noteSkipped || noteId !== null, false, noteSkipped),
        summary: noteSkipped ? "Skipped because the case document upload was not confirmed." : "To the Notes section of the patient chart, add the following note(s):",
        content: noteSkipped ? undefined : <div className="space-y-3"><div className="rounded-md border bg-muted/30 px-3 py-2"><CopyableContent text="Referral document uploaded to attachments." alwaysShow={true} /></div><DecisionMetadata items={[{
            label: "Clinicient note ID",
            value: noteId ?? "Not recorded"
          }]} />{ineligibleNote ? <div className="space-y-2 border-t pt-3"><p className="text-sm font-medium">This referral is ineligible for scheduling — also add this note:</p><div className="rounded-md border bg-muted/30 px-3 py-2"><CopyableContent text={ineligibleNote.note} alwaysShow={true} /></div><DecisionMetadata items={[{
              label: "Clinicient note ID",
              value: ineligibleNote.noteEhrId
            }]} /></div> : null}{missingPhoneNote ? <div className="space-y-2 border-t pt-3"><p className="text-sm font-medium">The referral has no phone number — also add this note:</p><div className="rounded-md border bg-muted/30 px-3 py-2"><CopyableContent text={missingPhoneNote.note} alwaysShow={true} /></div><DecisionMetadata items={[{
              label: "Clinicient note ID",
              value: missingPhoneNote.noteEhrId
            }]} /></div> : null}</div>
      },
      [StepKey.CLINICIENT_SYNC_INSURANCE]: {
        title: "Add primary insurance to case",
        status: stepStatus(insuranceStep, insuranceSkipped || insuranceStep?.output?.outcome === "synced" && insurancePlanId !== null, false, insuranceSkipped),
        summary: insuranceSkipped ? "Preserve the matched case\u2019s existing insurance. Verify coverage in Clinicient." : insuranceStep?.output?.outcome === "waiting" ? "Waiting for another referral for this patient to supply primary insurance." : "Ensure that all of the following information has been added to the case. The subscriber is always configured as Self. Add secondary insurance if needed \u2014 secondary insurance must be added manually.",
        content: insuranceSkipped ? undefined : <div className="space-y-3"><FieldLayout hideEmptyFields={false} fields={primaryInsuranceFields} patient={referral} />{secondaryEhrPayerName || referral.secondaryCoverage ? <div className="space-y-2"><p className="text-sm font-medium">Secondary insurance (add manually)</p><FieldLayout hideEmptyFields={false} fields={secondaryInsuranceFields} patient={referral} /></div> : null}<DecisionMetadata items={[{
            label: "Clinicient insurance plan ID",
            value: insurancePlanId ?? "Not recorded"
          }]} /></div>
      },
      [StepKey.CLINICIENT_ENSURE_AUTHORIZATION]: {
        title: "Add authorization number to case, if needed.",
        status: stepStatus(authorizationStep, authorizationSkipped || authorizationId !== null, false, authorizationSkipped),
        summary: authorizationSkipped ? "No authorization number was provided for this referral." : "If the incoming referral includes an authorization number, it must be added to the case (unless the same authorization is already listed in the case). If start and end date are not listed, use today's date for both. If number of visits is not listed, use 1 as a placeholder.",
        content: authorizationSkipped ? undefined : <div className="space-y-3"><FieldLayout hideEmptyFields={false} fields={authorizationFields} patient={referral} /><DecisionMetadata items={[{
            label: "Clinicient authorization ID",
            value: authorizationId ?? "Not recorded"
          }, {
            label: "Authorization",
            value: recordOrigin(authorizationStep?.output?.created)
          }]} /></div>
      },
      [StepKey.CLINICIENT_AUTHORIZATION_DOCUMENT_UPLOAD]: {
        title: "Attach the referral document to the authorization",
        status: stepStatus(authorizationUploadStep, authorizationUploadSkipped || authorizationDocumentId !== null, false, authorizationUploadSkipped),
        summary: authorizationUploadRejected ? "The authorization document upload was rejected. The workflow continued." : authorizationUploadSkipped ? "No authorization attachment is needed for this referral." : "Unless there is a matching authorization number with a document already attached (check by clicking the paperclip button next to the authorization information), the document must be uploaded as an attachment to the authorization by clicking this paperclip button.",
        content: authorizationUploadSkipped || authorizationUploadRejected ? undefined : <DecisionMetadata items={[{
          label: "Clinicient authorization document ID",
          value: authorizationDocumentId ?? "Not recorded"
        }, {
          label: "Attachment",
          value: recordOrigin(authorizationUploadStep?.output?.uploaded)
        }]} />
      },
      [StepKey.CLINICIENT_AUTHORIZATION_TASK]: {
        title: "Create the authorization task",
        status: stepStatus(authorizationTaskStep, authorizationTaskSkipped || authorizationTaskId !== null, false, authorizationTaskSkipped),
        summary: authorizationTaskSkipped ? "For newly-created cases or referral documents that do not contain an authorization number, no authorization task is required." : "If the document is a Continuation of Care, create a \"Re-Authorization\" task. If the document is not, create an \"Authorization\" task.",
        content: authorizationTaskSkipped ? undefined : <div className="space-y-3"><FieldLayout hideEmptyFields={false} fields={authorizationTaskFields} patient={referral} /><DecisionMetadata items={[{
            label: "Clinicient task ID",
            value: authorizationTaskId ?? "Not recorded"
          }]} /></div>
      },
      [StepKey.REFERRAL_SHEET_EXPORT]: {
        title: "Export to the referral spreadsheet",
        status: stepStatus(exportStep, exportRange !== null, false, exportSkippedForExistingAppointment),
        summary: exportSkippedForExistingAppointment ? "Skipped spreadsheet export because the case already has an upcoming appointment within 90 days." : exportStep?.status === TaskStepStatus.COMPLETED ? "The referral row was exported and confirmed in the spreadsheet." : exportStep?.status === TaskStepStatus.FAILED ? "Export failed. Fix the issue and retry before verifying this referral." : "The final automated step exports this referral to the tracking spreadsheet.",
        content: exportStep?.status === TaskStepStatus.FAILED ? <ReferralExportResult key={task.updatedAt} task={task} /> : exportRange && !exportSkippedForExistingAppointment ? <DecisionMetadata items={[{
          label: "Spreadsheet row",
          value: exportRange
        }]} /> : undefined
      }
    };
    let t42;
    if ($[92] !== task.files) {
      t42 = task.files.find(_temp5)?.note;
      $[92] = task.files;
      $[93] = t42;
    } else {
      t42 = $[93];
    }
    note = t42;
    const informationNeededStep = onHold ? lastStep : undefined;
    const stepKeys = getConfluentReferralStepKeys(orderedTaskSteps.map(_temp6));
    hasInformationNeededStep = stepKeys.some(key_2 => key_2 === informationNeededStep?.stepKey);
    steps = stepKeys.map(key_3 => {
      const step_1 = definitions[key_3];
      const isBlockingStep = key_3 === lastStep?.stepKey && (onHold || task.status === TaskStatus.FAILED);
      const needsAction = key_3 === informationNeededStep?.stepKey;
      return {
        ...step_1,
        status: step_1.status === "failed" ? "failed" : needsAction ? "needs_action" : step_1.status,
        content: needsAction ? <><WorkflowStepInformationNeeded note={note} />{step_1.content}</> : step_1.content,
        continuation: isBlockingStep && !pendingApproval && lastStep && key_3 !== StepKey.REFERRAL_SHEET_EXPORT ? {
          instruction: "After fixing the issue, continue from this step. Completed steps will be preserved.",
          control: <WorkflowStepResumeButton key={`${lastStep.id}:${task.updatedAt}`} task={task} step={lastStep} />
        } : undefined
      };
    });
    const allSynced = steps.every(_temp7);
    isCompleted = task.status === TaskStatus.COMPLETED;
    const isProcessing = task.status === TaskStatus.PROCESSING;
    let t43;
    if ($[94] !== taskSteps) {
      t43 = taskSteps.some(_temp8);
      $[94] = taskSteps;
      $[95] = t43;
    } else {
      t43 = $[95];
    }
    const hasFailedStep = t43;
    let t44;
    if ($[96] !== taskSteps) {
      t44 = taskSteps.some(_temp9);
      $[96] = taskSteps;
      $[97] = t44;
    } else {
      t44 = $[97];
    }
    const hasTimedHold = t44;
    let t45;
    if ($[98] !== taskSteps) {
      t45 = taskSteps.some(_temp10);
      $[98] = taskSteps;
      $[99] = t45;
    } else {
      t45 = $[99];
    }
    const hasAsyncStep = t45;
    const workflowNeedsAttention = onHold || hasFailedStep;
    workflowContinuing = isProcessing || hasAsyncStep || hasTimedHold;
    const canVerify = task.status === TaskStatus.VERIFICATION_NEEDED && allSynced && !pendingApproval && !workflowNeedsAttention && !hasAsyncStep && !hasTimedHold;
    needsAttention = workflowNeedsAttention || Boolean(pendingApproval) || steps.some(_temp11);
    let t46;
    if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
      t46 = <p className="text-sm">Verify the insurance and referral documents in Clinicient. Complete any remaining secondary insurance and authorization work, then use Correct or Incorrect at the bottom to complete this task.</p>;
      $[100] = t46;
    } else {
      t46 = $[100];
    }
    steps.push({
      title: "Verify in Clinicient",
      status: isCompleted ? "verified" : hasFailedStep ? "failed" : workflowNeedsAttention || pendingApproval || canVerify ? "needs_action" : "waiting",
      summary: isCompleted ? "This task has been marked complete." : pendingApproval ? "Complete the pending workflow confirmation before verifying this task." : workflowNeedsAttention ? "Resolve the outstanding workflow issue before verifying this task." : workflowContinuing ? "The workflow is continuing. Verify the results when it finishes." : !allSynced ? "Resolve the incomplete sync steps above before verifying this task." : "Confirm the patient, case, referral document, chart note, and insurance in Clinicient and complete the remaining manual work.",
      content: t46
    });
    $[0] = pendingApproval;
    $[1] = referral;
    $[2] = task;
    $[3] = taskSteps;
    $[4] = authorizationUploadNeedsReport;
    $[5] = hasInformationNeededStep;
    $[6] = isCompleted;
    $[7] = needsAttention;
    $[8] = note;
    $[9] = steps;
    $[10] = uploadNeedsReport;
    $[11] = workflowContinuing;
  } else {
    authorizationUploadNeedsReport = $[4];
    hasInformationNeededStep = $[5];
    isCompleted = $[6];
    needsAttention = $[7];
    note = $[8];
    steps = $[9];
    uploadNeedsReport = $[10];
    workflowContinuing = $[11];
  }
  let t1;
  if ($[101] !== authorizationUploadNeedsReport || $[102] !== task.displayId || $[103] !== task.id || $[104] !== uploadNeedsReport) {
    t1 = uploadNeedsReport || authorizationUploadNeedsReport ? <div className="rounded-md border border-warn/30 bg-warn/10 px-3 py-3 text-sm"><p className="font-semibold">Report this referral to the Confluent team.</p><p>Clinicient did not confirm the {uploadNeedsReport ? "case record upload" : ""}{uploadNeedsReport && authorizationUploadNeedsReport ? " or " : ""}{authorizationUploadNeedsReport ? "authorization attachment upload" : ""}. Include referral task{" "}{task.displayId ?? task.id}.</p></div> : undefined;
    $[101] = authorizationUploadNeedsReport;
    $[102] = task.displayId;
    $[103] = task.id;
    $[104] = uploadNeedsReport;
    $[105] = t1;
  } else {
    t1 = $[105];
  }
  const t2 = !isCompleted && taskSteps.length === 0 && !needsAttention ? "No Clinicient sync steps have been recorded for this task yet." : undefined;
  let t3;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-sm text-muted-foreground">SMS eligibility is assessed on the Scheduling Eligibility Verification task after this referral is completed.</p>;
    $[106] = t3;
  } else {
    t3 = $[106];
  }
  let t4;
  if ($[107] !== hasInformationNeededStep || $[108] !== needsAttention || $[109] !== note) {
    t4 = needsAttention && note && !hasInformationNeededStep ? <p className="whitespace-pre-wrap text-sm">{note}</p> : null;
    $[107] = hasInformationNeededStep;
    $[108] = needsAttention;
    $[109] = note;
    $[110] = t4;
  } else {
    t4 = $[110];
  }
  let t5;
  if ($[111] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[111] = t4;
    $[112] = t5;
  } else {
    t5 = $[112];
  }
  let t6;
  if ($[113] !== steps || $[114] !== t1 || $[115] !== t2 || $[116] !== t5 || $[117] !== workflowContinuing) {
    t6 = <ReferralWorkflowTimeline steps={steps} isProcessing={workflowContinuing} priorityContent={t1} waitingMessage={t2} summaryContent={t5} />;
    $[113] = steps;
    $[114] = t1;
    $[115] = t2;
    $[116] = t5;
    $[117] = workflowContinuing;
    $[118] = t6;
  } else {
    t6 = $[118];
  }
  return t6;
}
function _temp11(step_6) {
  return step_6.status === "needs_action" || step_6.status === "failed";
}
function _temp10(step_5) {
  return step_5.status === TaskStepStatus.ASYNC;
}
function _temp9(step_4) {
  return step_4.status === TaskStepStatus.PAUSED;
}
function _temp8(step_3) {
  return step_3.status === TaskStepStatus.FAILED;
}
function _temp7(step_2) {
  return step_2.status === "automated" || step_2.status === "not_needed";
}
function _temp6(step_0) {
  return step_0.stepKey;
}
function _temp5(file) {
  return file.type === FileType.INBOUND;
}
function _temp4() {
  return "Front Desk";
}
function _temp3() {
  return "Medium";
}
function _temp2(phone) {
  return phone?.trim();
}
function _temp(a, b) {
  return a.stepIndex - b.stepIndex;
}